import React, { useState, useRef, useEffect, useLayoutEffect, memo } from "react";
import { useStyles } from "./styles";
import { buildTextList, buildMatrix, percentColor } from "./utils";

export default memo(function SimilarityChord({ comparison, onCellHover, onCellClick, threshold, dynamicRibbonWidth }) {
    const [lo, hi] = Array.isArray(threshold) ? threshold : [0, threshold ?? 0];
    const classes = useStyles();
    const texts = buildTextList(comparison.sources);
    const matrix = buildMatrix(texts, comparison.sources);
    const n = texts.length;
    const wrapperRef = useRef(null);
    const svgRef = useRef(null);
    const tooltipRef = useRef(null);
    const [dims, setDims] = useState({ width: 400, height: 400 });
    const [isResizing, setIsResizing] = useState(false);
    const debounceRef = useRef(null);
    const lastHoverRef = useRef({ i: -1, j: -1, label: -1 });
    const dataRef = useRef({ texts, matrix, onCellHover, onCellClick });
    dataRef.current = { texts, matrix, onCellHover, onCellClick };

    useEffect(() => {
        const el = wrapperRef.current;
        if (!el) return;
        // Read initial size
        const { width, height } = el.getBoundingClientRect();
        if (width && height) setDims({ width, height });

        const ro = new ResizeObserver((entries) => {
            const entry = entries[0];
            const w = entry.contentRect.width;
            const h = entry.contentRect.height;
            if (!w || !h) return;
            setIsResizing(true);
            clearTimeout(debounceRef.current);
            debounceRef.current = setTimeout(() => {
                setDims({ width: w, height: h });
                setIsResizing(false);
            }, 150);
        });
        ro.observe(el);
        return () => { ro.disconnect(); clearTimeout(debounceRef.current); };
    }, [comparison.id]);

    const { width, height } = dims;
    const cx = width / 2;
    const cy = height / 2;
    const minDim = Math.min(width, height);
    const fontSize = Math.max(10, Math.min(13, minDim * 0.028));
    const LABEL_RESERVE = Math.max(70, minDim * 0.20);
    const R = Math.max(40, minDim / 2 - LABEL_RESERVE);
    const SLOT = (2 * Math.PI) / n;
    const ARC_GAP_FRAC = n > 30 ? 0.08 : n > 15 ? 0.12 : 0.18;
    // gap between adjacent arc endpoints in pixels = SLOT * ARC_GAP_FRAC * R
    // round cap extends ARC_W/2 beyond each endpoint, so max ARC_W = gap to prevent overlap
    const maxArcW = SLOT * ARC_GAP_FRAC * R;
    const ARC_W = Math.max(2, Math.min(18, R * 0.06, maxArcW));
    const CP_FRAC = 0.35;

    const nodeColor = (i) => `hsl(${Math.round(360 * i / n)}, 68%, 42%)`;
    const ang = (i) => SLOT * i - Math.PI / 2;
    const pt = (θ, r) => [cx + r * Math.cos(θ), cy + r * Math.sin(θ)];

    const arcPath = (i) => {
        const halfGap = (SLOT * ARC_GAP_FRAC) / 2;
        const a1 = ang(i) - SLOT / 2 + halfGap;
        const a2 = ang(i) + SLOT / 2 - halfGap;
        const [x1, y1] = pt(a1, R);
        const [x2, y2] = pt(a2, R);
        const largeArc = a2 - a1 > Math.PI ? 1 : 0;
        return `M ${x1} ${y1} A ${R} ${R} 0 ${largeArc} 1 ${x2} ${y2}`;
    };

    const ri = R - ARC_W; // ribbon outer edge = inner face of arc (fixed)

    // Returns [a1, a2] angles for node i's ribbon endpoint, scaled by val% when dynamic.
    const ribbonAngles = (i, val) => {
        const halfGap = (SLOT * ARC_GAP_FRAC) / 2;
        const fullHalfAngle = SLOT / 2 - halfGap;
        if (!dynamicRibbonWidth) return [ang(i) - fullHalfAngle, ang(i) + fullHalfAngle];
        // Scale angular span by val/100; minimum 2px arc length at ri
        const minHalfAngle = 1 / ri; // 2px total → 1px each side
        const scaledHalfAngle = Math.max(minHalfAngle, fullHalfAngle * (val / 100));
        return [ang(i) - scaledHalfAngle, ang(i) + scaledHalfAngle];
    };

    const ribbonPath = (i, j, valI, valJ) => {
        const [a1i, a2i] = ribbonAngles(i, valI);
        const [a1j, a2j] = ribbonAngles(j, valJ);
        const [xi1, yi1] = pt(a1i, ri);
        const [xi2, yi2] = pt(a2i, ri);
        const [xj1, yj1] = pt(a1j, ri);
        const [xj2, yj2] = pt(a2j, ri);
        const lai = a2i - a1i > Math.PI ? 1 : 0;
        const laj = a2j - a1j > Math.PI ? 1 : 0;
        // bezier: from arc i's far edge → arc j's near edge, then j's arc, then back
        const cp = (x, y) => [cx + (x - cx) * CP_FRAC, cy + (y - cy) * CP_FRAC];
        const [cpx1, cpy1] = cp(xi2, yi2);
        const [cpx2, cpy2] = cp(xj1, yj1);
        const [cpx3, cpy3] = cp(xj2, yj2);
        const [cpx4, cpy4] = cp(xi1, yi1);
        return [
            `M ${xi1} ${yi1}`,
            `A ${ri} ${ri} 0 ${lai} 1 ${xi2} ${yi2}`,
            `C ${cpx1} ${cpy1},${cpx2} ${cpy2},${xj1} ${yj1}`,
            `A ${ri} ${ri} 0 ${laj} 1 ${xj2} ${yj2}`,
            `C ${cpx3} ${cpy3},${cpx4} ${cpy4},${xi1} ${yi1}`,
            "Z",
        ].join(" ");
    };

    const labelInfo = (i) => {
        const θ = ang(i);
        const r = R + ARC_W / 2 + 7;
        const x = cx + r * Math.cos(θ);
        const y = cy + r * Math.sin(θ);
        const rightHalf = Math.cos(θ) >= 0;
        let rot = θ * (180 / Math.PI);
        if (!rightHalf) rot += 180;
        return { x, y, rot, anchor: rightHalf ? "start" : "end" };
    };

    const connections = [];
    for (let i = 0; i < n; i++) {
        for (let j = i + 1; j < n; j++) {
            const cell = matrix[i][j];
            if (cell !== null && cell.p1 >= lo && cell.p1 <= hi) connections.push({ i, j, val: cell.p1, val2: cell.p2 ?? cell.p1 });
        }
    }
    connections.sort((a, b) => a.val - b.val); // draw weakest first so strong ones render on top

    const maxLabelChars = Math.max(6, Math.floor(LABEL_RESERVE / (fontSize * 0.62)));
    const shortName = (name) =>
        name.length > maxLabelChars + 1 ? name.slice(0, maxLabelChars) + "…" : name;

    useEffect(() => {
        const svg = svgRef.current;
        if (!svg) return;

        function setHighlight(i, j, on) {
            if (i < 0) return;
            const conn = svg.querySelector(`path[data-ci="${i}"][data-cj="${j}"]`);
            if (conn) {
                conn.setAttribute("stroke", on ? "#1565c0" : "none");
                conn.setAttribute("stroke-width", on ? "2" : "0");
                conn.setAttribute("opacity", on ? "1" : "0.65");
                if (on) {
                    const g = svg.querySelector("g.chords");
                    if (g) g.appendChild(conn);
                }
            }
            [i, j].forEach(idx => {
                const arc = svg.querySelector(`path[data-arc="${idx}"]`);
                if (arc) arc.setAttribute("stroke", on ? "#1565c0" : nodeColor(idx));
                const lbl = svg.querySelector(`text[data-lbl="${idx}"]`);
                if (lbl) {
                    lbl.setAttribute("fill", on ? "#1565c0" : "#333");
                    lbl.setAttribute("font-weight", on ? "700" : "400");
                }
            });
        }

        function setLabelHighlight(idx, on) {
            for (let j = 0; j < n; j++) {
                if (j === idx) continue;
                const ci = Math.min(idx, j), cj = Math.max(idx, j);
                const conn = svg.querySelector(`path[data-ci="${ci}"][data-cj="${cj}"]`);
                if (conn) {
                    conn.setAttribute("stroke", on ? "#1565c0" : "none");
                    conn.setAttribute("stroke-width", on ? "2" : "0");
                    conn.setAttribute("opacity", on ? "1" : "0.65");
                    if (on) {
                        const chordG = svg.querySelector("g.chords");
                        if (chordG) chordG.appendChild(conn);
                    }
                }
            }
            const arc = svg.querySelector(`path[data-arc="${idx}"]`);
            if (arc) arc.setAttribute("stroke", on ? "#1565c0" : nodeColor(idx));
            const lbl = svg.querySelector(`text[data-lbl="${idx}"]`);
            if (lbl) {
                lbl.setAttribute("fill", on ? "#1565c0" : "#333");
                lbl.setAttribute("font-weight", on ? "700" : "400");
            }
            for (let j = 0; j < n; j++) {
                if (j === idx) continue;
                const ci = Math.min(idx, j), cj = Math.max(idx, j);
                if (svg.querySelector(`path[data-ci="${ci}"][data-cj="${cj}"]`)) {
                    const connLbl = svg.querySelector(`text[data-lbl="${j}"]`);
                    if (connLbl) {
                        connLbl.setAttribute("fill", on ? "#1565c0" : "#333");
                        connLbl.setAttribute("font-weight", on ? "700" : "400");
                    }
                }
            }
        }

        function moveTooltip(e) {
            const tip = tooltipRef.current;
            if (!tip) return;
            const offset = 14;
            const tw = tip.offsetWidth, th = tip.offsetHeight;
            let x = e.clientX + offset;
            let y = e.clientY + offset;
            if (x + tw > window.innerWidth - 8) x = e.clientX - tw - offset;
            if (y + th > window.innerHeight - 8) y = e.clientY - th - offset;
            tip.style.left = `${x}px`;
            tip.style.top = `${y}px`;
        }

        function showTooltip(e, i, j) {
            const tip = tooltipRef.current;
            if (!tip) return;
            const { matrix: m, texts: t } = dataRef.current;
            const cell = m[i][j];
            const p1 = cell ? cell.p1 : null;
            const p2 = cell ? cell.p2 : null;
            const mw = cell ? cell.mw : null;
            const fmtLine = (tx, pct) => {
                const matchStr = mw != null && tx.words != null ? `${mw}/${tx.words} Words` : null;
                const pctStr = pct != null ? `(${pct.toFixed(1)}%)` : null;
                const stat = [matchStr, pctStr].filter(Boolean).join(" ");
                return stat ? `${tx.name}\n${stat}` : tx.name;
            };
            tip.textContent = `${fmtLine(t[i], p1)}\n↕\n${fmtLine(t[j], p2)}`;
            tip.style.display = "block";
            moveTooltip(e);
        }

        function hideTooltip() {
            const tip = tooltipRef.current;
            if (tip) tip.style.display = "none";
        }

        function showLabelTooltip(e, idx) {
            const tip = tooltipRef.current;
            if (!tip) return;
            const { matrix: m, texts: t } = dataRef.current;
            const name = t[idx] ? t[idx].name : "";
            let maxSim = null;
            let count = 0;
            for (let j = 0; j < n; j++) {
                if (j === idx) continue;
                const val = m[idx][j];
                if (val !== null) {
                    count++;
                    if (maxSim === null || val.p1 > maxSim) maxSim = val.p1;
                }
            }
            const maxStr = maxSim !== null ? `${maxSim.toFixed(1)}%` : "n/a";
            tip.textContent = `${name}\nMax. Similarity: ${maxStr}\nNumber of related texts: ${count}`;
            tip.style.display = "block";
            moveTooltip(e);
        }

        function handleMouseOver(e) {
            const path = e.target.closest("path[data-ci]");
            const labelElem = !path ? e.target.closest("text[data-lbl]") : null;
            const arcElem = (!path && !labelElem) ? e.target.closest("path[data-arc]") : null;
            const last = lastHoverRef.current;
            const newI = path ? parseInt(path.dataset.ci, 10) : -1;
            const newJ = path ? parseInt(path.dataset.cj, 10) : -1;
            const newLabel = labelElem ? parseInt(labelElem.dataset.lbl, 10)
                           : arcElem ? parseInt(arcElem.dataset.arc, 10)
                           : -1;
            if (newI === last.i && newJ === last.j && newLabel === last.label) return;
            if (last.label >= 0) setLabelHighlight(last.label, false);
            else setHighlight(last.i, last.j, false);
            if (newLabel >= 0) {
                setLabelHighlight(newLabel, true);
                lastHoverRef.current = { i: -1, j: -1, label: newLabel };
                showLabelTooltip(e, newLabel);
                const { onCellHover: cb } = dataRef.current;
                if (cb) cb(null);
            } else {
                setHighlight(newI, newJ, true);
                lastHoverRef.current = { i: newI, j: newJ, label: -1 };
                if (newI >= 0) {
                    showTooltip(e, newI, newJ);
                    const { matrix: m, texts: t, onCellHover: cb } = dataRef.current;
                    const hCell = m[newI][newJ];
                    if (cb) cb({ r: newI, c: newJ, val: hCell ? hCell.p1 : null, val2: hCell ? hCell.p2 : null, mw: hCell ? hCell.mw : null, rowName: t[newI].name, colName: t[newJ].name, rowWords: t[newI].words, colWords: t[newJ].words });
                } else {
                    hideTooltip();
                    const { onCellHover: cb } = dataRef.current;
                    if (cb) cb(null);
                }
            }
        }

        function handleMouseMove(e) {
            if (lastHoverRef.current.i >= 0 || lastHoverRef.current.label >= 0) moveTooltip(e);
        }

        function handleMouseLeave() {
            const last = lastHoverRef.current;
            if (last.label >= 0) setLabelHighlight(last.label, false);
            else setHighlight(last.i, last.j, false);
            lastHoverRef.current = { i: -1, j: -1, label: -1 };
            hideTooltip();
            const { onCellHover: cb } = dataRef.current;
            if (cb) cb(null);
        }

        function handleClick(e) {
            const path = e.target.closest("path[data-ci]");
            if (!path) return;
            const i = parseInt(path.dataset.ci, 10);
            const j = parseInt(path.dataset.cj, 10);
            const { onCellClick: cb } = dataRef.current;
            if (cb) cb({ r: i, c: j });
        }

        svg.addEventListener("mouseover", handleMouseOver);
        svg.addEventListener("mousemove", handleMouseMove);
        svg.addEventListener("mouseleave", handleMouseLeave);
        svg.addEventListener("click", handleClick);
        return () => {
            svg.removeEventListener("mouseover", handleMouseOver);
            svg.removeEventListener("mousemove", handleMouseMove);
            svg.removeEventListener("mouseleave", handleMouseLeave);
            svg.removeEventListener("click", handleClick);
            hideTooltip();
        };
    }, [comparison.id]);

    if (n === 0) return null;

    return (
        <>
            <div ref={tooltipRef} className={classes.heatmapTooltip} />
            <div ref={wrapperRef} className={classes.heatmapWrapper}>
                <svg ref={svgRef} width="100%" height="100%" viewBox={`0 0 ${width} ${height}`} preserveAspectRatio="xMidYMid meet" style={{ display: "block", fontFamily: "inherit" }}>
                    {/* Chords */}
                    <g className="chords">
                        {connections.map(({ i, j, val, val2 }) => (
                            <path
                                key={`${i}-${j}`}
                                d={ribbonPath(i, j, val, val2)}
                                fill={percentColor(val)}
                                stroke="none"
                                strokeWidth={2}
                                opacity={0.65}
                                data-ci={i}
                                data-cj={j}
                                style={{ cursor: "default" }}
                            />
                        ))}
                    </g>
                    {/* Arcs */}
                    <g>
                        {texts.map((t, i) => (
                            <path
                                key={t.id}
                                d={arcPath(i)}
                                fill="none"
                                stroke={nodeColor(i)}
                                strokeWidth={ARC_W}
                                strokeLinecap="round"
                                data-arc={i}
                                style={{ cursor: "default", pointerEvents: "stroke" }}
                            />
                        ))}
                    </g>
                    {/* Labels */}
                    {!isResizing && texts.map((t, i) => {
                        const { x, y, rot, anchor } = labelInfo(i);
                        return (
                            <text
                                key={t.id}
                                x={x} y={y}
                                transform={`rotate(${rot},${x},${y})`}
                                textAnchor={anchor}
                                dominantBaseline="middle"
                                fontSize={fontSize}
                                fontWeight={400}
                                fill="#333"
                                data-lbl={i}
                                style={{ cursor: "default" }}
                            >
                                {shortName(t.name)}
                            </text>
                        );
                    })}
                </svg>
            </div>
        </>
    );
});
